import { environment } from '@view-app/environment'
import { kebabCase } from 'lodash'
import { EnvironmentVariables, environmentMeta } from '../environments/environment-variables'

/**
 * Initialize the application
 *
 * Sets up any configurations required by the application at startup
 *
 * @param document  The injected document
 *
 * @returns         A factory that sets up the application
 */
export function initializeAppFactory(document: Document) {
  return () => {
    loadEnvironmentVariablesFromAttributes(document)
    loadEnvironmentVariablesFromConfig(document)
  }
}

/**
 * Register any attributes on the root component as environment variables
 *
 * @param document  The injected document
 *
 * @returns         The modified environment variables
 */
function loadEnvironmentVariablesFromAttributes(document: Document): EnvironmentVariables {
  // Get the possible attributes that can be mapped to the environment variables
  let appElement = document.querySelector('gpei-root')

  let environmentAttributes = Object
    .keys(environment)
    .reduce(
      (collection: Map<string, string>, it: string) => collection.set(it, kebabCase(it)),
      new Map()
    )

  // Assign the environment variables
  for (let [ variable, attribute ] of environmentAttributes) {
    if (appElement.hasAttribute(attribute)) {
      let pendingValue: unknown = appElement.getAttribute(attribute)

      // Apply modification or massaging to certain values
      if (environmentMeta[variable].type == 'boolean' && typeof pendingValue === 'string') {
        pendingValue = pendingValue.toLocaleLowerCase() !== 'false' && pendingValue.toLocaleLowerCase() !== '0'
      }

      environment[variable] = pendingValue
    }
  }



  return environment
}

/**
 * Register any configs provided via the AngularAppConfig property on the Window
 * as environment variables
 *
 * @param document  The injected document
 *
 * @returns         The modified environment variables
 */
function loadEnvironmentVariablesFromConfig(document: Document): EnvironmentVariables {
  let configs = document.defaultView['AngularAppConfig']

  if (configs) {
    environment.defaultService = configs.FeatureEndpoint ? configs.FeatureEndpoint : environment.defaultService
    environment.validator = configs.ValidatorEndpoint ? configs.ValidatorEndpoint : environment.validator
    environment.saveLocation = configs.SaveLocationEndpoint ? configs.SaveLocationEndpoint : environment.saveLocation
    environment.serviceId = configs.ServiceId ? configs.ServiceId : environment.serviceId
    environment.preventInitialLoad = configs.PreventInitialLoad ? configs.PreventInitialLoad : environment.preventInitialLoad
    environment.workflowHost = configs.WorkflowHost ? configs.WorkflowHost : environment.workflowHost
  }

  return environment
}
